import {
  deleteObject,
  ref,
  uploadBytesResumable,
  type FirebaseStorage,
} from 'firebase/storage'
import type { RcFile } from 'antd/es/upload'
import { getFileMetadata } from '../../lib/shared/getContentDisposition'
import { OUTBOUND_ATTACHMENT_PREFIX } from '../../lib/shared/chatwootAttachments'

// A staging upload that stops making progress is indistinguishable from a hung
// one, and `uploadBytes` has no timeout of its own — so a stalled upload used to
// spin the composer forever with no error, no Sentry event and no server log.
// We watch `bytesTransferred` instead of capping total duration: a
// genuinely slow 25 MB upload keeps ticking and must be allowed to finish, while
// a dead connection goes quiet. No progress within this window aborts the send.
export const STAGE_STALL_TIMEOUT_MS = 45_000

// How far the stalled file had got when the watchdog fired. A connection that
// never came up (`bytesTransferred` 0) and one that flushed every byte without
// ever being acknowledged (equal to `totalBytes`) are indistinguishable from
// the composer, and have different causes.
export interface StalledUploadContext {
  bytesTransferred: number
  totalBytes: number
  fileIndex: number
  fileCount: number
}

// Thrown when the stall watchdog above fires. `code` mirrors the Firebase
// Storage convention so errorResponse/getLocalizedErrorMessage can map it the
// same way it maps `storage/retry-limit-exceeded`, and `sentryContext` is the
// conventional property errorResponse forwards into the Sentry extra.
//
// Deliberately carries no filename: this error reaches Sentry via errorResponse,
// and an operator's attachment is routinely named after an employee
// (e.g. "Juli_001_Ansfelde,Sandra_2026_04_BEABESCH.pdf"). §203 — keep personal
// data out of logs. The same reason the backend omits the path when it logs a
// failed cleanup. Sizes and counts carry no such data.
export class AttachmentUploadStalledError extends Error {
  readonly code = 'storage/upload-stalled'
  readonly sentryContext: StalledUploadContext

  // "Attachment", not "staging": the message becomes the Sentry issue title,
  // where "staging" reads as the environment rather than the upload step.
  constructor(context: StalledUploadContext) {
    super('Attachment upload stalled')
    this.name = 'AttachmentUploadStalledError'
    this.sentryContext = context
  }
}

// Firebase Storage puts the full object path in some error messages
// (`storage/unauthorized` and `storage/object-not-found` both do), and our paths
// end in the operator's original filename. Keep the folder — it identifies the
// conversation and is useful in Sentry — and drop the leaf. §203, as above.
export function redactAttachmentFileName(message: string): string {
  return message.replace(
    new RegExp(
      `(${OUTBOUND_ATTACHMENT_PREFIX}(?:/[^/\\s'"]+){3}/)[^\\s'"]+`,
      'g'
    ),
    '$1<redacted>'
  )
}

// Preserve `code` and `name` — errorResponse maps on `code`, and the composers
// detect an operator-initiated cancel by `name === 'AbortError'` — while
// replacing the message with a path-free version.
function withRedactedMessage(error: unknown): unknown {
  if (
    !error ||
    typeof error !== 'object' ||
    typeof (error as { message?: unknown }).message !== 'string'
  ) {
    return error
  }
  const original = (error as { message: string }).message
  const redacted = redactAttachmentFileName(original)
  if (redacted === original) {
    return error
  }
  const copy = new Error(redacted)
  copy.name = (error as Error).name
  Object.assign(copy, { code: (error as { code?: unknown }).code })
  return copy
}

export interface StagingProgress {
  bytesTransferred: number
  totalBytes: number
}

interface StageChatwootAttachmentsArgs {
  storage: FirebaseStorage
  uid: string
  conversationId: string
  files: RcFile[]
  // Called as bytes land so the caller can render a determinate progress bar
  // rather than an unbounded spinner. Reports the batch total, not per file.
  onProgress?: (progress: StagingProgress) => void
  // Lets the operator abort a send that is going nowhere. Aborting cancels every
  // in-flight upload; the already-finished ones are cleaned up by the caller.
  signal?: AbortSignal
}

// Best-effort delete; a failed cleanup must never mask the caller's real error.
export async function deleteChatwootAttachments(
  storage: FirebaseStorage,
  paths: string[]
): Promise<void> {
  await Promise.all(
    paths.map((path) => deleteObject(ref(storage, path)).catch(() => undefined))
  )
}

// One file, with the stall watchdog and abort wired to the resumable task.
// Resolves with the Storage path; rejects with the upload error, an
// AttachmentUploadStalledError, or the signal's abort reason.
function uploadOne(
  storage: FirebaseStorage,
  path: string,
  file: RcFile,
  position: { index: number; count: number },
  onBytes: (bytesTransferred: number) => void,
  signal?: AbortSignal
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    // Before creating the task, not after: `uploadBytesResumable` starts writing
    // immediately, so an already-aborted signal would otherwise upload the file
    // in full and then reject without ever returning its path — leaving an
    // orphan in Storage that the caller's cleanup can't see.
    if (signal?.aborted) {
      reject(signal.reason ?? new Error('Upload aborted'))
      return
    }

    const task = uploadBytesResumable(
      ref(storage, path),
      file,
      getFileMetadata(file.name, file.type)
    )

    let settled = false
    let stallTimer: number | undefined
    let bytesTransferred = 0
    // Set by the watchdog / abort listener so the task's own error callback
    // (which fires with `storage/canceled` right after we cancel) reports the
    // real reason instead of the cancellation it triggered.
    let abortReason: unknown = null

    function finish(run: () => void) {
      if (settled) {
        return
      }
      settled = true
      window.clearTimeout(stallTimer)
      signal?.removeEventListener('abort', onAbort)
      run()
    }

    function armStallTimer() {
      window.clearTimeout(stallTimer)
      stallTimer = window.setTimeout(() => {
        abortReason = new AttachmentUploadStalledError({
          bytesTransferred,
          totalBytes: file.size,
          fileIndex: position.index,
          fileCount: position.count,
        })
        // Cancelling routes us through the task's error callback below, which
        // rejects with `abortReason`.
        task.cancel()
      }, STAGE_STALL_TIMEOUT_MS)
    }

    function onAbort() {
      abortReason = signal?.reason ?? new Error('Upload aborted')
      task.cancel()
    }

    signal?.addEventListener('abort', onAbort, { once: true })
    armStallTimer()

    task.on(
      'state_changed',
      (snapshot) => {
        bytesTransferred = snapshot.bytesTransferred
        onBytes(snapshot.bytesTransferred)
        // Progress means the connection is alive — restart the countdown.
        armStallTimer()
      },
      (error) => {
        finish(() => reject(abortReason ?? withRedactedMessage(error)))
      },
      () => {
        finish(() => resolve(task.snapshot.ref.fullPath))
      }
    )
  })
}

// Stage each file under the operator's own outbound folder and return the
// Storage paths for the callable to forward. The trailing path segment keeps
// the original filename so Chatwoot shows a meaningful name.
//
// allSettled (not Promise.all) is deliberate: Promise.all rejects on the first
// failure while sibling uploads are still in flight, so any upload that lands
// after the cleanup runs would be orphaned. allSettled waits for every upload
// to settle, then — if any failed — removes the ones that succeeded and rethrows
// the original error, leaving nothing behind in Storage.
export async function stageChatwootAttachments({
  storage,
  uid,
  conversationId,
  files,
  onProgress,
  signal,
}: StageChatwootAttachmentsArgs): Promise<string[]> {
  if (files.length === 0) {
    return []
  }

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
  // Per-file counters, summed on every tick: a resumable task can report a
  // *lower* bytesTransferred after an internal retry, so accumulating deltas
  // would drift the batch total upwards.
  const transferred = new Array<number>(files.length).fill(0)
  // Report 0% before anything starts, so the caller can show the progress bar
  // and its cancel button from the moment the send begins rather than only once
  // the first bytes land — a connection that dies immediately never reports any.
  onProgress?.({ bytesTransferred: 0, totalBytes })

  const results = await Promise.allSettled(
    files.map((file, index) => {
      const path = `${OUTBOUND_ATTACHMENT_PREFIX}/${uid}/${conversationId}/${crypto.randomUUID()}/${file.name}`
      return uploadOne(
        storage,
        path,
        file,
        { index, count: files.length },
        (bytesTransferred) => {
          transferred[index] = bytesTransferred
          onProgress?.({
            bytesTransferred: transferred.reduce((sum, n) => sum + n, 0),
            totalBytes,
          })
        },
        signal
      )
    })
  )

  const uploadedPaths = results
    .filter(
      (result): result is PromiseFulfilledResult<string> =>
        result.status === 'fulfilled'
    )
    .map((result) => result.value)

  const failure = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  )
  if (failure) {
    await deleteChatwootAttachments(storage, uploadedPaths)
    throw failure.reason
  }

  return uploadedPaths
}
